import { useMemo } from "react";
import { TrendingUp, TrendingDown, Activity } from "lucide-react";
import { fmtUsd } from "@/lib/api";
import { cn } from "@/lib/utils";

// Real-time total P&L of LIVE Gate.io positions, recomputed from the freshest
// ticker prices (updates every ~5s with the price poll).
export const LivePnlCard = ({ portfolio, prices = [] }) => {
  const stats = useMemo(() => {
    const positions = (portfolio?.positions || []).filter((p) => p.live);
    const priceOf = (sym) => {
      const t = prices.find((x) => x.symbol === sym);
      return t?.price ?? null;
    };
    let mv = 0, cost = 0;
    const rows = positions.map((p) => {
      const cur = priceOf(p.symbol) ?? p.current_price ?? p.avg_price;
      const posMv = p.qty * cur;
      const posCost = p.qty * p.avg_price;
      mv += posMv; cost += posCost;
      const pnl = posMv - posCost;
      return { symbol: p.symbol, pnl, pct: posCost ? (pnl / posCost) * 100 : 0, mv: posMv };
    }).sort((a, b) => Math.abs(b.mv) - Math.abs(a.mv));
    const pnl = mv - cost;
    return { pnl, pct: cost ? (pnl / cost) * 100 : 0, mv, count: positions.length, rows };
  }, [portfolio, prices]);

  if (!portfolio?.live_mode) return null;
  const pos = stats.pnl >= 0;

  return (
    <div
      data-testid="live-pnl-card"
      className={cn(
        "relative overflow-hidden rounded-xl border p-5 sm:p-6",
        pos ? "border-[#00FFA3]/30 bg-[#00FFA3]/[0.04]" : "border-[#FF3366]/30 bg-[#FF3366]/[0.04]"
      )}
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="relative flex h-2.5 w-2.5">
              <span className={cn("animate-ping absolute inline-flex h-full w-full rounded-full opacity-60",
                pos ? "bg-[var(--pos)]" : "bg-[var(--neg)]")} />
              <span className={cn("relative inline-flex rounded-full h-2.5 w-2.5", pos ? "bg-[var(--pos)]" : "bg-[var(--neg)]")} />
            </span>
            <span className="text-[11px] uppercase tracking-[0.15em] text-[var(--text-dim)]">
              Canlı Kâr / Zarar · Anlık
            </span>
          </div>
          <div className={cn("font-mono text-3xl sm:text-4xl font-black tracking-tight",
            pos ? "text-[var(--pos)]" : "text-[var(--neg)]")} data-testid="live-pnl-total">
            {pos ? "+" : ""}{fmtUsd(stats.pnl)}
          </div>
          <div className={cn("mt-1 text-sm font-mono flex items-center gap-1.5",
            pos ? "text-[var(--pos)]" : "text-[var(--neg)]")}>
            {pos ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
            {pos ? "+" : ""}{stats.pct.toFixed(2)}%
            <span className="text-[var(--text-faint)]">· {stats.count} canlı pozisyon · {fmtUsd(stats.mv)}</span>
          </div>
        </div>

        {stats.rows.length > 0 && (
          <div className="flex flex-wrap gap-2 sm:justify-end sm:max-w-[55%]">
            {stats.rows.slice(0, 5).map((r) => {
              const up = r.pnl >= 0;
              return (
                <div key={r.symbol} data-testid={`live-pnl-chip-${r.symbol}`}
                  className={cn("rounded-md border px-2.5 py-1.5 text-xs font-mono",
                    up ? "border-[#00FFA3]/25 bg-[#00FFA3]/[0.06]" : "border-[#FF3366]/25 bg-[#FF3366]/[0.06]")}>
                  <span className="font-head font-bold text-[var(--text-primary)]">{r.symbol}</span>
                  <span className={cn("ml-1.5", up ? "text-[var(--pos)]" : "text-[var(--neg)]")}>
                    {up ? "+" : ""}{r.pct.toFixed(1)}%
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {stats.count === 0 && (
        <p className="mt-2 text-xs text-[var(--text-faint)] flex items-center gap-1.5">
          <Activity className="h-3.5 w-3.5" /> Henüz açık canlı pozisyon yok — bot uygun alım noktasında gerçek pozisyon açtığında burada görünür.
        </p>
      )}
    </div>
  );
};
